import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { CourseRoster, getCourseRoster, RosterEntry } from '../api/course-roster';
import { formatMoney } from '../../../commodity/format/money';
import { ENROLLMENT_STATE_LABEL } from '../components/enrollmentRequest';

/**
 * S5.5 (TCH-09) — the course as its teacher sees it.
 *
 * ADR-007 carries ten requirements against this story and the Wave A audit
 * found no screen at all: a teacher could publish a course and then had no way
 * to learn who had enrolled, how far along each child was, or when they next
 * met. Everything here answers one of those three questions.
 */

const fmtDate = (iso?: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('es-MX', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
};

const STATE_LABELS: Record<string, string> = ENROLLMENT_STATE_LABEL;

const TeacherCourseDetail: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();

  const [roster, setRoster] = useState<CourseRoster | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!courseId) return;
    setLoading(true);
    getCourseRoster(courseId)
      .then((r) => {
        if (active) {
          setRoster(r);
          setError(null);
        }
      })
      .catch((e: any) => {
        if (active) setError(e?.message || 'No pudimos cargar este curso.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [courseId]);

  if (loading) return <p>Cargando curso…</p>;
  if (error || !roster) {
    return (
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <p style={{ color: '#c92a2a' }}>{error || 'Curso no encontrado.'}</p>
        <button onClick={() => navigate('/teacher/courses')} style={linkBtn}>
          Volver a mis cursos
        </button>
      </div>
    );
  }

  const seatsLeft = Math.max(0, roster.capacity - roster.seatsTaken);

  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <button onClick={() => navigate('/teacher/courses')} style={linkBtn}>
        &larr; Mis cursos
      </button>

      <header style={{ marginBottom: 20 }}>
        <h1 style={{ margin: '0 0 6px' }}>{roster.title}</h1>
        <p style={{ margin: 0, color: '#666' }}>
          {roster.subject} · {roster.duration} min ·{' '}
          {roster.offerMode === 'ONE_ON_ONE' ? 'Individual' : 'Grupo'} ·{' '}
          {formatMoney(roster.price, roster.currency)} ·{' '}
          {/*
            Whether the course ends, and when (TCH-09-FR-10). The course model
            has carried this since the offer model shipped; this screen simply
            never said it, so a family could not tell an eight-week cohort from
            something open-ended.
          */}
          {roster.durationMode === 'FIXED' && roster.durationWeeks
            ? `${roster.durationWeeks} semanas`
            : 'Sin fecha de término'}
        </p>
        {roster.description && (
          <p style={{ margin: '10px 0 0', color: '#333', maxWidth: 680 }}>{roster.description}</p>
        )}
      </header>

      <div style={statsRow}>
        <Stat label="Estado" value={roster.status} />
        <Stat label="Inscritos" value={`${roster.seatsTaken} / ${roster.capacity}`} />
        {/*
          Learners is not seatsTaken: a checkout still in flight holds a seat
          without anyone having been taught, so the two differ on purpose.
        */}
        <Stat label="Alumnos" value={String(roster.learners)} />
        <Stat
          label="Avance promedio"
          value={
            roster.completionRate === null
              ? 'Sin datos'
              : `${Math.round(roster.completionRate * 100)}%`
          }
        />
        <Stat
          label="Lugares libres"
          value={String(seatsLeft)}
          tone={seatsLeft === 0 ? '#c92a2a' : '#2b8a3e'}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <button onClick={() => navigate(`/teacher/courses/${roster.courseId}/edit`)} style={btn}>
          Editar curso
        </button>
        <button
          onClick={() => navigate('/teacher/availability')}
          style={{ ...btn, background: '#495057' }}
        >
          Mi disponibilidad
        </button>
      </div>

      <h2 style={{ fontSize: 18, margin: '0 0 10px' }}>Alumnos ({roster.students.length})</h2>

      {roster.students.length === 0 ? (
        <p style={emptyStyle}>
          Todavía nadie se ha inscrito en este curso.
          {roster.status !== 'PUBLISHED' &&
            ' Publícalo para que aparezca en el catálogo y las familias puedan encontrarlo.'}
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr style={{ borderBottom: '2px solid #d1d5da', textAlign: 'left' }}>
                <th style={thStyle}>Alumno</th>
                <th style={thStyle}>Estado</th>
                <th style={thStyle}>Progreso</th>
                <th style={thStyle}>Próxima sesión</th>
                <th style={thStyle} />
              </tr>
            </thead>
            <tbody>
              {roster.students.map((s) => (
                <StudentRow key={s.enrollmentId} student={s} navigate={navigate} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const StudentRow: React.FC<{
  student: RosterEntry;
  navigate: (to: string) => void;
}> = ({ student, navigate }) => {
  return (
    <tr style={{ borderBottom: '1px solid #e9ecef' }}>
      <td style={tdStyle}>
        <strong>{student.studentFirstName}</strong>
        {typeof student.studentGrade === 'number' && student.studentGrade > 0 && (
          <span style={{ color: '#666', fontSize: 13 }}> · {student.studentGrade}º</span>
        )}
      </td>
      <td style={tdStyle}>{STATE_LABELS[student.state] ?? student.state}</td>
      <td style={tdStyle}>
        {/* Sessions, as sessions: CROSS VI.4 Consistency. No percent worked out here. */}
        <span style={{ fontSize: 13, color: '#666', whiteSpace: 'nowrap' }}>
          {student.sessionsCompleted} de {student.sessionsTotal} sesiones
        </span>
      </td>
      <td style={tdStyle}>
        {student.nextSessionAt ? (
          <>
            {fmtDate(student.nextSessionAt)}
            {student.nextSessionSequence && (
              <span style={{ color: '#666', fontSize: 13 }}>
                {' '}
                · sesión {student.nextSessionSequence}
              </span>
            )}
          </>
        ) : (
          <span style={{ color: '#666' }}>Sin agendar</span>
        )}
      </td>
      <td style={tdStyle}>
        <button
          onClick={() => navigate(`/enrollments/${student.enrollmentId}`)}
          style={{ ...btn, padding: '6px 10px', fontSize: 13 }}
        >
          Ver inscripción
        </button>
      </td>
    </tr>
  );
};

const Stat: React.FC<{ label: string; value: string; tone?: string }> = ({
  label,
  value,
  tone,
}) => (
  <div style={statStyle}>
    <div style={{ fontSize: 12, color: '#666' }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 700, color: tone ?? '#161e22' }}>{value}</div>
  </div>
);

const linkBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#0366d6',
  cursor: 'pointer',
  padding: 0,
  marginBottom: 16,
};

const btn: React.CSSProperties = {
  padding: '8px 14px',
  border: 'none',
  borderRadius: 6,
  background: '#1971c2',
  color: '#fff',
  fontSize: 14,
  cursor: 'pointer',
};

const statsRow: React.CSSProperties = {
  display: 'flex',
  gap: 12,
  marginBottom: 20,
  flexWrap: 'wrap',
};

const statStyle: React.CSSProperties = {
  border: '1px solid #d1d5da',
  borderRadius: 8,
  padding: 16,
  minWidth: 140,
  flex: 1,
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  minWidth: 620,
};

const thStyle: React.CSSProperties = { padding: 8, fontSize: 13, color: '#666' };
const tdStyle: React.CSSProperties = { padding: 8, verticalAlign: 'middle' };

const emptyStyle: React.CSSProperties = {
  border: '1px dashed #d1d5da',
  borderRadius: 8,
  padding: 20,
  color: '#666',
  textAlign: 'center',
};

export default TeacherCourseDetail;
